import type { WorkflowConfigInput } from '@automata/wfb-api';
import {
  ExecutorOptionsInput,
  ParameterDefinition,
  PlannerOptionsInput,
  TransportMatrixInput,
  WorkflowInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import uniqWith from 'lodash/uniqWith';

import { err, ok } from './utils';
import {
  LATEST_WORKFLOW_MIGRATION_VERSION,
  LoadedWorkflowLatest,
} from '../../migration';
import type {
  JsonLabwareById,
  MaybeWorkflowConfig,
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../shared/types';
import { WorkflowState } from '../workflow/types';

const normalizePlanningTargetsForApi = (
  planningTargets:
    | WorkflowConfigInput['workflow']['planning_targets']
    | undefined
): WorkflowConfigInput['workflow']['planning_targets'] | undefined => {
  if (planningTargets === undefined) return undefined;

  const consistency = planningTargets.consistency;
  if (Array.isArray(consistency)) {
    return {
      ...planningTargets,
      consistency: {
        default: consistency,
      },
    };
  }

  return planningTargets;
};

// 20 seconds is ALOE's default transport_matrix time.
export const ALOE_TRANSPORT_MATRIX: TransportMatrixInput = {
  default: 35,
  paths: [],
  overrides: [
    {
      src_instrument_id: 'biotek',
      dst_instrument_id: 'envision',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'biotek',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [17],
    },
    {
      src_instrument_id: 'biotek',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [15],
    },
    {
      src_instrument_id: 'biotek',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'bin',
      units: ['robot_1'],
      time: [20],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [21],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'stacker_b',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'biotek',
      units: ['robot_1'],
      time: [20],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'envision',
      units: ['robot_1'],
      time: [17],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [19],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [17],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'plateloc',
      units: ['robot_1'],
      time: [16],
    },
    {
      src_instrument_id: 'hamilton',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [19],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'bin',
      units: ['robot_1'],
      time: [14],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'biotek',
      units: ['robot_1'],
      time: [16],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'envision',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'stacker_b',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'multidrop',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'multidrop_2',
      dst_instrument_id: 'bin',
      units: ['robot_1'],
      time: [38],
    },
    {
      src_instrument_id: 'multidrop_2',
      dst_instrument_id: 'envision',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'multidrop_2',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [19],
    },
    {
      src_instrument_id: 'multidrop_2',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'multidrop_2',
      dst_instrument_id: 'stacker_b',
      units: ['robot_1'],
      time: [22],
    },
    {
      src_instrument_id: 'plateloc',
      dst_instrument_id: 'bin',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'plateloc',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [15],
    },
    {
      src_instrument_id: 'plateloc',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [12],
    },
    {
      src_instrument_id: 'stacker_a',
      dst_instrument_id: 'plateloc',
      units: ['robot_1'],
      time: [14],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'biotek',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [23],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [14],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [17],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'plateloc',
      units: ['robot_1'],
      time: [18],
    },
    {
      src_instrument_id: 'stacker_b',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [15],
    },
    {
      src_instrument_id: 'xpeel',
      dst_instrument_id: 'biotek',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'xpeel',
      dst_instrument_id: 'hamilton',
      units: ['robot_1'],
      time: [14],
    },
    {
      src_instrument_id: 'xpeel',
      dst_instrument_id: 'multidrop',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'xpeel',
      dst_instrument_id: 'multidrop_2',
      units: ['robot_1'],
      time: [11],
    },
    {
      src_instrument_id: 'envision',
      dst_instrument_id: 'plateloc',
      units: ['robot_1'],
      time: [16],
    },
    {
      src_instrument_id: 'plateloc',
      dst_instrument_id: 'stacker_a',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'plateloc',
      dst_instrument_id: 'stacker_b',
      units: ['robot_1'],
      time: [13],
    },
    {
      src_instrument_id: 'stacker_a',
      dst_instrument_id: 'xpeel',
      units: ['robot_1'],
      time: [14],
    },
    {
      src_instrument_id: 'xpeel',
      dst_instrument_id: 'envision',
      units: ['robot_1'],
      time: [15],
    },
  ],
};

export const ULEX_TRANSPORT_MATRIX: TransportMatrixInput = {
  default: 35,
  paths: [],
  overrides: [
    {
      src_instrument_id: 'biomek_i5a_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [27],
    },
    {
      src_instrument_id: 'biomek_i5a_1',
      dst_instrument_id: 'cytomatC450_1',
      units: ['reuben_robot'],
      time: [28],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [23],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'cytomatC450_1',
      units: ['reuben_robot'],
      time: [25],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'pherastar_1',
      units: ['reuben_robot'],
      time: [18],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'sealer_1',
      units: ['reuben_robot'],
      time: [48],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'multidrop_1',
      units: ['reuben_robot'],
      time: [25],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'centrifuge_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'multidrop_1',
      dst_instrument_id: 'cytomatC450_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'cytomat10_1',
      dst_instrument_id: 'biomek_i5a_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'cytomat10_1',
      dst_instrument_id: 'biomek_i5b_1',
      units: ['reuben_robot'],
      time: [23],
    },
    {
      src_instrument_id: 'cytomat10_1',
      dst_instrument_id: 'peeler_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'centrifuge_1',
      dst_instrument_id: 'cytomatC450_1',
      units: ['reuben_robot'],
      time: [30],
    },
    {
      src_instrument_id: 'centrifuge_1',
      dst_instrument_id: 'biomek_i5b_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'cytomatC450_1',
      dst_instrument_id: 'biomek_i5a_1',
      units: ['reuben_robot'],
      time: [37],
    },
    {
      src_instrument_id: 'cytomatC450_1',
      dst_instrument_id: 'biomek_i5b_1',
      units: ['reuben_robot'],
      time: [27],
    },
    {
      src_instrument_id: 'peeler_1',
      dst_instrument_id: 'pherastar_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'pherastar_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [17],
    },
    {
      src_instrument_id: 'pherastar_1',
      dst_instrument_id: 'sealer_1',
      units: ['reuben_robot'],
      time: [52],
    },
    {
      src_instrument_id: 'sealer_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [54],
    },
    {
      src_instrument_id: 'biomek_i5a_1',
      dst_instrument_id: 'biomek_i5b_1',
      units: ['reuben_robot'],
      time: [23],
    },
    {
      src_instrument_id: 'biomek_i5a_1',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [47],
    },
    {
      src_instrument_id: 'biomek_i5b_1',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [42],
    },
    {
      src_instrument_id: 'biomek_i7_1',
      dst_instrument_id: 'peeler_1',
      units: ['reuben_robot'],
      time: [23],
    },
    {
      src_instrument_id: 'biomek_i7_1',
      dst_instrument_id: 'sealer_1',
      units: ['reuben_robot'],
      time: [52],
    },
    {
      src_instrument_id: 'biomek_i7_1',
      dst_instrument_id: 'uncapper_1',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'biomek_i7_1',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [44],
    },
    {
      src_instrument_id: 'centrifuge_1',
      dst_instrument_id: 'biomek_i7_1',
      units: ['reuben_robot'],
      time: [21],
    },
    {
      src_instrument_id: 'centrifuge_1',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'coldplate_1',
      dst_instrument_id: 'coldplate_2',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_1',
      dst_instrument_id: 'coldplate_3',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_1',
      dst_instrument_id: 'coldplate_4',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_2',
      dst_instrument_id: 'coldplate_3',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_2',
      dst_instrument_id: 'coldplate_4',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_3',
      dst_instrument_id: 'coldplate_4',
      units: ['reuben_robot'],
      time: [20],
    },
    {
      src_instrument_id: 'coldplate_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'coldplate_2',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'coldplate_3',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'coldplate_4',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'coldplate_1',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [50],
    },
    {
      src_instrument_id: 'coldplate_2',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [50],
    },
    {
      src_instrument_id: 'coldplate_3',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [50],
    },
    {
      src_instrument_id: 'coldplate_4',
      dst_instrument_id: 'washer_1',
      units: ['reuben_robot'],
      time: [50],
    },
    {
      src_instrument_id: 'cytomat10_1',
      dst_instrument_id: 'uncapper_1',
      units: ['reuben_robot'],
      time: [21],
    },
    {
      src_instrument_id: 'cytomatC425_1',
      dst_instrument_id: 'cytomat10_1',
      units: ['reuben_robot'],
      time: [47],
    },
    {
      src_instrument_id: 'cytomatC425_1',
      dst_instrument_id: 'cytomatC450_1',
      units: ['reuben_robot'],
      time: [49],
    },
    {
      src_instrument_id: 'cytomatC450_1',
      dst_instrument_id: 'uncapper_1',
      units: ['reuben_robot'],
      time: [23],
    },
    {
      src_instrument_id: 'multidrop_1',
      dst_instrument_id: 'pherastar_1',
      units: ['reuben_robot'],
      time: [19],
    },
    {
      src_instrument_id: 'pherastar_1',
      dst_instrument_id: 'cytomatC425_1',
      units: ['reuben_robot'],
      time: [47],
    },
    {
      src_instrument_id: 'sealer_1',
      dst_instrument_id: 'multidrop_1',
      units: ['reuben_robot'],
      time: [47],
    },
    {
      src_instrument_id: 'uncapper_1',
      dst_instrument_id: 'centrifuge_1',
      units: ['reuben_robot'],
      time: [21],
    },
    {
      src_instrument_id: 'uncapper_1',
      dst_instrument_id: 'coldplate_1',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'uncapper_1',
      dst_instrument_id: 'coldplate_2',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'uncapper_1',
      dst_instrument_id: 'coldplate_3',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'uncapper_1',
      dst_instrument_id: 'coldplate_4',
      units: ['reuben_robot'],
      time: [46],
    },
    {
      src_instrument_id: 'washer_1',
      dst_instrument_id: 'cytomatC425_1',
      units: ['reuben_robot'],
      time: [49],
    },
  ],
};

export const getHardcodedDefaultTransportMatrix = (
  selectedWorkcellId: string | null = null
): TransportMatrixInput => {
  if (selectedWorkcellId === 'az' || selectedWorkcellId === 'azProd') {
    return ALOE_TRANSPORT_MATRIX;
  }
  if (selectedWorkcellId === 'ucbProd') {
    return ULEX_TRANSPORT_MATRIX;
  }
  return {
    default: 35,
  };
};

export default function deriveWorkflowJson(
  workflowAuthor: string,
  workflowDescription: string,
  workflowName: string,
  parameterDefinitions: ParameterDefinition[],
  plannerOptions: PlannerOptionsInput | undefined,
  executorOptions: ExecutorOptionsInput | undefined,
  jsonInstruments: WorkflowConfigInput['workcell']['instruments'],
  jsonLabware: MaybeWorkflowSerializationErrors<JsonLabwareById>,
  jsonLabwareTypes: MaybeWorkflowSerializationErrors<
    WorkflowConfigInput['workflow']['labware_types']
  >,
  jsonTasks: MaybeWorkflowSerializationErrors<WorkflowInput['tasks']>,
  jsonTimeConstraints: MaybeWorkflowSerializationErrors<
    WorkflowConfigInput['workflow']['time_constraints']
  >,
  slotConflictErrors: WorkflowSerializationError[],
  schedulerVersion: string,
  selectedWorkcellId: string | null,
  planningTargets?: WorkflowConfigInput['workflow']['planning_targets']
): MaybeWorkflowConfig {
  const normalizedPlanningTargets =
    normalizePlanningTargetsForApi(planningTargets);
  let errors: WorkflowSerializationError[] = [...slotConflictErrors];

  if (jsonLabware.isError) {
    errors = [...errors, ...jsonLabware.errors];
  }

  if (jsonLabwareTypes.isError) {
    errors = [...errors, ...jsonLabwareTypes.errors];
  }

  if (jsonTasks.isError) {
    errors = [...errors, ...jsonTasks.errors];
  }

  if (jsonTimeConstraints.isError) {
    errors = [...errors, ...jsonTimeConstraints.errors];
  }

  if (
    jsonLabware.isError ||
    jsonLabwareTypes.isError ||
    jsonTasks.isError ||
    jsonTimeConstraints.isError ||
    slotConflictErrors.length > 0
  ) {
    return err(uniqWith(errors, isEqual));
  }

  return ok({
    name: workflowName,
    metadata: {
      author: workflowAuthor,
      description: workflowDescription,
      version: '0.1', // TODO(IL, 2024-04-09): versioning not implemented in UI
      workflow_type: 'canvas',
    },
    parameter_definitions: parameterDefinitions,
    workcell: {
      instruments: jsonInstruments,
      transport_matrix: getHardcodedDefaultTransportMatrix(selectedWorkcellId),
    },
    workflow: {
      labware: sortBy(Object.values(jsonLabware.data), 'id'),
      labware_types: jsonLabwareTypes.data,
      tasks: jsonTasks.data,
      time_constraints: jsonTimeConstraints.data,
      planning_targets: normalizedPlanningTargets,
    },
    options: { planner: plannerOptions, executor: executorOptions },
    scheduler_config: {
      scheduler: 'maestro',
      version: schedulerVersion,
    },
  });
}

export function deriveWorkflowJsonForSave(
  workflowAuthor: string,
  workflowDescription: string,
  workflowName: string,
  parameterDefinitions: ParameterDefinition[],
  plannerOptions: PlannerOptionsInput | undefined,
  executorOptions: ExecutorOptionsInput | undefined,
  jsonInstruments: WorkflowConfigInput['workcell']['instruments'],
  jsonLabware: JsonLabwareById,
  jsonLabwareTypes: WorkflowConfigInput['workflow']['labware_types'],
  jsonTasks: WorkflowInput['tasks'],
  graph: LoadedWorkflowLatest['graph'],
  visualWorkflow: LoadedWorkflowLatest['visualWorkflow'],
  timeConstraints: WorkflowConfigInput['workflow']['time_constraints'],
  workcellInstruments: WorkflowState['workcellInstruments'],
  workcellTransportConfigId: WorkflowState['workcellTransportConfigId'],
  workcellTransportConfigType: WorkflowState['workcellTransportConfigType'],
  workcellTransportConfigVersion: WorkflowState['workcellTransportConfigVersion'],
  selectedWorkcellId: WorkflowState['selectedWorkcellId'],
  maestroVersion: string,
  planningTargets?: WorkflowConfigInput['workflow']['planning_targets']
): WorkflowConfigInput {
  const normalizedPlanningTargets =
    normalizePlanningTargetsForApi(planningTargets);
  const persistedData = {
    graph,
    visualWorkflow,
    workcellInstruments: workcellInstruments,
    workcellTransportConfigId,
    workcellTransportConfigType,
    workcellTransportConfigVersion,
    selectedWorkcellId,
  } satisfies LoadedWorkflowLatest;

  return {
    name: workflowName,
    metadata: {
      author: workflowAuthor,
      description: workflowDescription,
      version: '0.1', // TODO(IL, 2024-04-09): versioning not implemented in UI
      workflow_type: 'canvas',
    },
    parameter_definitions: parameterDefinitions,
    workcell: {
      instruments: jsonInstruments,
      transport_matrix: getHardcodedDefaultTransportMatrix(selectedWorkcellId),
      workcell_id:
        workcellTransportConfigId && workcellTransportConfigType
          ? null
          : workcellTransportConfigId,
      ...(workcellTransportConfigId && workcellTransportConfigType
        ? {
            transport_config: {
              id: workcellTransportConfigId,
              type: workcellTransportConfigType,
              ...(workcellTransportConfigVersion !== undefined
                ? { version: workcellTransportConfigVersion }
                : {}),
            },
          }
        : {}),
    },
    workflow: {
      labware: sortBy(Object.values(jsonLabware), 'id'),
      labware_types: jsonLabwareTypes,
      tasks: jsonTasks,
      time_constraints: timeConstraints,
      planning_targets: normalizedPlanningTargets,
      layout: {
        version: LATEST_WORKFLOW_MIGRATION_VERSION.toString(),
        data: persistedData as any, // NOTE(IL, 2024-09-04): any cast due to typing problems with JsonSerializableInput
      },
      batches: visualWorkflow.batches,
    },
    options: {
      planner: plannerOptions,
      executor: executorOptions,
    },
    scheduler_config: {
      scheduler: 'maestro',
      version: maestroVersion,
    },
  };
}
